import { useEffect } from 'react';
import {
  CANONICAL_ORIGIN,
  DEFAULT_OG_IMAGE,
  ORGANIZATION_SCHEMA,
  WEBSITE_SCHEMA,
  toAbsoluteUrl,
} from '@/lib/seo';

interface FAQItem {
  question: string;
  answer: string;
}

interface HowToStep {
  name: string;
  text: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  article?: {
    publishedTime: string;
    modifiedTime?: string;
    author: string;
    section?: string;
    tags?: string[];
  };
  breadcrumbs?: Array<{ name: string; url: string }>;
  faq?: FAQItem[];
  howTo?: {
    name: string;
    description: string;
    totalTime?: string;
    steps: HowToStep[];
  };
  course?: {
    name: string;
    description: string;
    provider: string;
    url: string;
  };
  noindex?: boolean;
}

export const SEOHead = ({
  title,
  description,
  canonicalUrl,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  article,
  breadcrumbs,
  faq,
  howTo,
  course,
  noindex = false,
}: SEOHeadProps) => {
  useEffect(() => {
    document.title = title.length > 60 ? `${title.substring(0, 57)}...` : title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const setLink = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    const absoluteCanonicalUrl = canonicalUrl || `${CANONICAL_ORIGIN}${window.location.pathname}`;
    const truncatedDesc = description.length > 160 ? `${description.substring(0, 157)}...` : description;

    setMeta('description', truncatedDesc);
    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setLink('canonical', absoluteCanonicalUrl);

    setMeta('og:type', ogType, true);
    setMeta('og:title', title, true);
    setMeta('og:description', truncatedDesc, true);
    setMeta('og:image', toAbsoluteUrl(ogImage), true);
    setMeta('og:url', absoluteCanonicalUrl, true);
    setMeta('og:site_name', 'GoCoachify', true);
    setMeta('og:locale', 'en_US', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', truncatedDesc);
    setMeta('twitter:image', toAbsoluteUrl(ogImage));

    if (article && ogType === 'article') {
      setMeta('article:published_time', article.publishedTime, true);
      if (article.modifiedTime) setMeta('article:modified_time', article.modifiedTime, true);
      setMeta('article:author', article.author, true);
      if (article.section) setMeta('article:section', article.section, true);
      article.tags?.forEach((tag, index) => setMeta(`article:tag:${index}`, tag, true));
    }

    document.querySelectorAll('script[data-seo-jsonld]').forEach((node) => node.remove());

    const jsonLdData: Record<string, unknown>[] = [
      ORGANIZATION_SCHEMA as unknown as Record<string, unknown>,
      WEBSITE_SCHEMA as unknown as Record<string, unknown>,
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description: truncatedDesc,
        url: absoluteCanonicalUrl,
        isPartOf: { '@id': CANONICAL_ORIGIN },
      },
    ];

    if (breadcrumbs && breadcrumbs.length > 0) {
      jsonLdData.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${CANONICAL_ORIGIN}${item.url}`,
        })),
      });
    }

    if (article && ogType === 'article') {
      jsonLdData.push({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: title,
        description: truncatedDesc,
        image: toAbsoluteUrl(ogImage),
        author: { '@type': 'Organization', name: article.author },
        publisher: {
          '@type': 'Organization',
          name: 'GoCoachify',
          logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE },
        },
        datePublished: article.publishedTime,
        dateModified: article.modifiedTime || article.publishedTime,
        mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteCanonicalUrl },
        articleSection: article.section,
        keywords: article.tags?.join(', '),
      });
    }

    if (faq?.length) {
      jsonLdData.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      });
    }

    if (howTo?.steps?.length) {
      jsonLdData.push({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: howTo.name,
        description: howTo.description,
        totalTime: howTo.totalTime,
        step: howTo.steps.map((step) => ({ '@type': 'HowToStep', name: step.name, text: step.text })),
      });
    }

    if (course) {
      jsonLdData.push({
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: course.name,
        description: course.description,
        provider: {
          '@type': 'Organization',
          name: course.provider,
          sameAs: course.url,
        },
      });
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((node) => node.remove());
    };
  }, [title, description, canonicalUrl, ogType, ogImage, article, breadcrumbs, faq, howTo, course, noindex]);

  return null;
};

export default SEOHead;
